// ---- Tree-cutting (client) --------------------------------------------------
// Lightweight activity: with a hatchet on the held-tool channel, focus a chop
// tree and trigger Cut (F / action bar). Server locks the tree, streams
// chopOn {seed}, both sides expand the same pattern, client mounts the chop
// minigame and streams chopInput; server re-judges + spends stamina + awards
// logs + marks stump. Remotes see NetPlayer.chopping + chopSeq swings.
//
// Out of stamina ends the activity immediately (local unlock + toast; server
// ends with chopResult exhausted / frees the tree lock). Never leave
// game.chopping true without a live session — that flag is in inputLocked().
//
// Init AFTER initInteract, BEFORE initFocus (F / Esc precedence with minigame).

import { game } from "./state.ts";
import { getInteractProp } from "./interact.ts";
import { sendChopInput, sendChopEnd } from "./net.ts";
import { makeChopMinigame } from "./minigame/chop.ts";
import type { Minigame } from "./minigame/types.ts";
import { genChopPattern } from "./sim/chopping.ts";
import { CHOPPING_TUNING as CT } from "./tunables.ts";
import { startAttack } from "./player.ts";
import { flashToast } from "./inventory.ts";
import { playSfx, preloadSfx } from "./audio.ts";

/** Axe-on-wood one-shots in assets/audio/ — pick one at random per successful strike. */
const CHOP_SFX = ["chop1", "chop2", "chop3"] as const;

let session: {
  propId: string;
  x: number;
  z: number;
  mg: Minigame & { setStaminaLabel?(s: string): void };
} | null = null;

/** Suppress a second "too tired" toast when we already ended locally for stamina. */
let localExhaustToast = false;

export function isChoppingActive(): boolean {
  return !!session || game.chopping;
}

/** Fully stop the activity: free movement (clears game.chopping) + drop the lane. */
function teardownSession(): void {
  if (session) {
    session.mg.unmount();
    session = null;
  }
  game.chopping = false;
  if (game.player) {
    game.player.userData.chopping = false;
    game.player.userData.combatReady = false;
  }
}

/** Winded: toast, unlock movement, free the server lock. */
function endForExhaustion(): void {
  if (!session && !game.chopping) return;
  if (!localExhaustToast) {
    localExhaustToast = true;
    flashToast("You're too tired");
  }
  // chopInput for this swing is already on the wire; chopEnd frees the lock if
  // the server still holds a session after processing that input.
  sendChopEnd();
  teardownSession();
}

/** Server accepted the session — mount the minigame. */
export function onChopOn(propId: string, seed: number): void {
  if (session) {
    session.mg.unmount();
    session = null;
  }
  localExhaustToast = false;
  // Warm the three chop one-shots so the first key hit isn't silent.
  preloadSfx(...CHOP_SFX);
  const prop = getInteractProp(propId);
  const pattern = genChopPattern(seed, CT);
  const mg = makeChopMinigame({
    pattern,
    hitWindow: CT.hitWindow,
    tun: CT,
    title: "🪓 CHOP — match the keys!",
    staminaLabel: () =>
      `STA ${Math.max(0, Math.round(game.stamina))}/${game.maxStamina} (−${CT.chopStaCost}/swing)`,
    ev: {
      onInput(i, hit, finished) {
        // Already torn down (e.g. multi-timeout in one tick after exhaust) — ignore.
        if (!session) return;
        // Local feel: spend stamina + swing anim; server is authoritative.
        game.stamina = Math.max(0, game.stamina - CT.chopStaCost);
        startAttack(game.player, "primary");
        game.player.userData.combatReady = true;
        // Successful key strike → random axe-on-wood SFX (misses stay silent).
        if (hit) {
          playSfx(CHOP_SFX[(Math.random() * CHOP_SFX.length) | 0]!);
        }
        sendChopInput(i, hit);
        // Pattern still open and we can't afford another swing → stop NOW
        // (toast + unlock movement). If `finished`, wait for cut/failed result
        // instead of a false "too tired" on the last successful swing.
        // NOTE: this unmounts the minigame mid-tick; chop.ts must tolerate that
        // (do not touch arrow DOM after root is cleared — freezes the game loop).
        if (!finished && game.stamina < CT.chopStaCost) endForExhaustion();
      },
    },
  });
  mg.mount();
  session = {
    propId,
    x: prop?.x ?? game.player.position.x,
    z: prop?.z ?? game.player.position.z,
    mg,
  };
  game.chopping = true;
  game.player.userData.chopping = true;
  game.player.userData.combatReady = true; // hatchet ready stance while cutting
}

/** Server resolved the session. */
export function onChopResult(
  outcome: "cut" | "failed" | "invalid" | "exhausted",
  logs?: string,
  count?: number,
  sta?: number,
): void {
  const hadActivity = !!session || game.chopping || localExhaustToast;
  teardownSession();
  // Stamina echo: only ever pull the HUD *down*. After a cut the client has
  // already predicted spends; the server pool can be higher (fewer accepted
  // swings, or a cancel/invalid freeing a still-full gate). Overwriting with
  // that higher value looked like "instant refill to 4–5" on exhaust.
  if (typeof sta === "number" && Number.isFinite(sta)) {
    const s = Math.max(0, Math.min(game.maxStamina, sta));
    if (hadActivity) {
      game.stamina = Math.min(game.stamina, s);
    } else {
      // Begin rejected while idle (bar looked full, server empty) — absolute snap.
      game.stamina = s;
    }
  }
  if (outcome === "cut") {
    if (logs && count && count > 0) flashToast(`Chopped ${count} logs`);
  } else if (outcome === "failed") {
    flashToast("Too many misses — tree stands");
  } else if (outcome === "exhausted") {
    // Skip if we already toasted in endForExhaustion (avoids double flash).
    if (hadActivity && !localExhaustToast) flashToast("You're too tired");
  }
  localExhaustToast = false;
  void logs;
}

/** Cancel mid-session (Esc / walk-off). */
export function cancelChop(): void {
  if (!session && !game.chopping) return;
  localExhaustToast = false;
  sendChopEnd();
  teardownSession();
}

export function initWoodcutting(): void {
  addEventListener("keydown", (e) => {
    if (e.key === "Escape" && (session || game.chopping)) {
      cancelChop();
      e.stopImmediatePropagation();
      return;
    }
    if (session?.mg.key(e)) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);
}

export function updateWoodcutting(dt: number): void {
  // Safety: never stay movement-locked without a live minigame (stuck recovery).
  if (game.chopping && !session) {
    teardownSession();
    return;
  }
  if (!session) return;
  session.mg.tick(dt);
  // Walk too far from the tree → cancel.
  const q = game.player.position;
  const reach = 6;
  if (Math.hypot(q.x - session.x, q.z - session.z) > reach) {
    cancelChop();
    return;
  }
  // Face the tree while locked.
  game.player.rotation.y = Math.atan2(session.x - q.x, session.z - q.z);
  // Keep ready stance.
  game.player.userData.combatReady = true;
  game.player.userData.chopping = true;
}

export function resetWoodcutting(): void {
  localExhaustToast = false;
  teardownSession();
}
